"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";

interface CardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  items: string[];
  color: "orange" | "teal";
  className?: string;
}

const Card: React.FC<CardProps> = ({
  title,
  description,
  icon,
  items,
  color,
  className,
}) => {
  const [isActive, setIsActive] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1280); // 1024px is the lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle interactions based on device type
  const handleInteractionStart = () => {
    if (isMobile) {
      setIsActive((prev) => !prev); // Toggle on mobile/tablet
    } else {
      setIsActive(true); // Just set true on desktop
    }
  };

  const handleInteractionEnd = () => {
    if (!isMobile) {
      setIsActive(false); // Only reset on desktop
    }
  };

  // Define shadow classes based on color
  const shadowClasses =
    color === "orange"
      ? "shadow-[0px_0px_12px_0px_#C746011F] md:shadow-[0px_0px_25px_0px_#C746011F]"
      : "shadow-[0px_0px_12px_0px_#00797D1F] md:shadow-[0px_0px_25px_0px_#00797D1F]";

  return (
    <div
      className={cn(
        "group relative flex min-h-[344px] w-full min-w-[302px] max-w-[302px] flex-col items-center rounded-3xl border border-solid px-4 py-6 transition-all duration-300 ease-in-out md:min-h-[384px]",
        // Base color styling
        color === "orange"
          ? "border-primary-300 bg-primary-100"
          : "border-secondary-300 bg-secondary-100",
        // Shadow styling - apply when not hovered
        !isActive && shadowClasses,
        // Remove shadow on hover
        "hover:shadow-none",
        className
      )}
      onMouseEnter={() => !isMobile && handleInteractionStart()}
      onMouseLeave={() => !isMobile && handleInteractionEnd()}
      onClick={() => isMobile && handleInteractionStart()}
      role={isMobile ? "button" : undefined}
      tabIndex={isMobile ? 0 : undefined}
    >
      {/* Front face */}
      <div
        className={cn(
          "flex flex-col items-center gap-2 transition-all duration-500 ease-in-out md:gap-4",
          isActive
            ? "scale-95 transform opacity-0"
            : "scale-100 transform opacity-100"
        )}
        aria-hidden={isActive}
      >
        <div className="size-[200px] md:size-[224px]">{icon}</div>
        <div className="flex flex-col gap-2 text-center">
          <h3 className="font-montserrat text-subheading-2 text-neutral-600 md:text-subheading-1">
            {title}
          </h3>
          <p className="font-roboto text-body-1-300 text-neutral-600">
            {description}
          </p>
        </div>
      </div>

      {/* Back face */}
      <div
        className={cn(
          "absolute inset-0 flex flex-col gap-2 p-3 transition-all duration-500 ease-in-out md:gap-4 md:px-4 md:py-6",
          isActive
            ? "scale-100 transform opacity-100"
            : "scale-100 transform opacity-0"
        )}
        aria-hidden={!isActive}
      >
        <h3 className="text-center font-montserrat text-subheading-3-500 text-neutral-600 md:text-subheading-2">
          {title}
        </h3>

        <div className="flex w-full flex-col gap-2">
          {items.map((item, index) => {
            // Calculate the appropriate delay class based on index
            let delayClass = "";
            if (index === 0) delayClass = "delay-0";
            else if (index === 1) delayClass = "delay-75";
            else if (index === 2) delayClass = "delay-150";
            else if (index === 3) delayClass = "delay-300";
            else if (index === 4) delayClass = "delay-500";
            else if (index === 5) delayClass = "delay-700";
            else delayClass = "delay-[900ms]";

            return (
              <div
                key={index}
                className={cn(
                  "dela flex items-start gap-2 transition-all duration-500 ease-in-out",
                  isActive
                    ? `translate-y-0 transform opacity-100 ${delayClass}`
                    : "translate-y-2 transform opacity-0"
                )}
              >
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <path
                    d="M20.9219 5.99805L9.92188 16.998L4.92188 11.998"
                    stroke={color === "orange" ? "#E56E2E" : "#06A6AB"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="flex flex-col gap-2 font-roboto text-body-1-300 text-neutral-600">
                  {item}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export interface CareersCIEnvironmentProps {
  id?: string;
  className?: string;
  benefitsItems?: { item: string }[];
  qualificationsItems?: { item: string }[];
  responsibilitiesItems?: { item: string }[];
  benefitsTitle?: string;
  benefitsDescription?: string;
  qualificationsTitle?: string;
  qualificationsDescription?: string;
  responsibilitiesTitle?: string;
  responsibilitiesDescription?: string;
}

const CareersCIEnvironment: React.FC<CareersCIEnvironmentProps> = ({
  id = "ci-environment",
  className,
  benefitsItems = [
    { item: "Flexible scheduling" },
    { item: "Comprehensive benefits packages with 401(k) matching" },
    { item: "Paid mileage for travel" },
    { item: "Extensive training programs and 24/7 support" },
    { item: "Opportunities for career advancement" },
    { item: "Employee discounts with partner organizations" },
  ],
  qualificationsItems = [
    { item: "Passion for working with seniors" },
    { item: "Cleared TB test & background check" },
    { item: "Legal authorization to work in the U.S." },
    { item: "Proficient in English" },
    { item: "Comfortable using a smartphone" },
    { item: "Ability to assist clients with mobility and physical transfers" },
    { item: "Proof of COVID-19 vaccinations" },
  ],
  responsibilitiesItems = [
    { item: "Adhere to company policies and procedures" },
    { item: "Assist seniors with daily living activities" },
    { item: "Provide light housekeeping and meal preparation" },
    { item: "Support mobility and physical assistance" },
    { item: "Offer respectful companionship and meaningful conversation" },
  ],
  benefitsTitle = "Benefits",
  benefitsDescription = "As a part of our team, you are entitled to various perks",
  qualificationsTitle = "Qualifications",
  qualificationsDescription = "All Care Indeed employees uphold the highest levels of excellence",
  responsibilitiesTitle = "Responsibilities",
  responsibilitiesDescription = "Lorem ipsum dolor sit amet consectetur.",
}) => {
  const [emblaRef] = useEmblaCarousel({
    align: "start",
    containScroll: false,
    dragFree: true,
  });

  const cards = [
    {
      title: benefitsTitle,
      description: benefitsDescription,
      color: "orange" as const,
      items: benefitsItems.map((benefit) => benefit.item),
      icon: (
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 224 224"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M116.758 24.8282L128.359 44.3928C129.011 45.4822 130.097 46.2665 131.357 46.5715L153.473 51.5825C156.254 52.1926 157.34 55.5913 155.472 57.7264L140.525 74.8074C139.7 75.766 139.265 77.0297 139.395 78.3369L141.481 100.995C141.742 103.828 138.874 105.919 136.267 104.786L115.411 95.81C114.238 95.2871 112.891 95.2871 111.718 95.81L90.862 104.786C88.255 105.919 85.3873 103.828 85.648 100.995L87.7336 78.3369C87.8639 77.0733 87.4294 75.766 86.6039 74.8074L71.6572 57.7264C69.7888 55.5913 70.8751 52.1926 73.6558 51.5825L95.7718 46.5715C97.0319 46.2665 98.1181 45.4822 98.7699 44.3928L110.371 24.8282C111.805 22.388 115.368 22.388 116.802 24.8282H116.758Z"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeLinejoin="round"
          />
          <path
            d="M38.3287 57.9443L46.6277 66.7027C47.4967 67.6177 48.7567 68.1842 50.0168 68.1842H62.0524C64.8766 68.1842 66.7015 71.2344 65.3546 73.7617L59.6192 84.3937C59.0109 85.5266 58.8805 86.8774 59.2716 88.0975L63.0083 99.601C63.8773 102.303 61.5744 105.004 58.7502 104.481L46.8884 102.303C45.6283 102.085 44.3248 102.346 43.282 103.13L33.5493 110.233C31.2464 111.889 27.9877 110.494 27.6401 107.662L26.0759 95.6793C25.9021 94.4157 25.2069 93.2392 24.2075 92.4984L14.4748 85.3959C12.1719 83.7401 12.4761 80.167 15.0831 78.9469L25.9455 73.7617C27.1187 73.1952 27.9877 72.193 28.3787 70.9729L32.1154 59.4694C32.9844 56.7678 36.4604 55.9835 38.4157 58.0315L38.3287 57.9443Z"
            stroke="#E56E2E"
            strokeWidth="5.6"
            strokeLinejoin="round"
          />
          <path
            d="M192.838 59.8615L197.009 71.1907C197.444 72.4108 198.356 73.3694 199.573 73.8923L210.653 78.6419C213.26 79.7748 213.738 83.3043 211.478 85.0472L202.006 92.5419C201.007 93.3263 200.355 94.5028 200.225 95.81L199.138 107.836C198.878 110.669 195.662 112.194 193.316 110.625L183.279 103.915C182.193 103.218 180.889 102.956 179.629 103.261L167.898 105.919C165.117 106.529 162.684 103.958 163.466 101.213L166.725 89.5789C167.072 88.3589 166.899 87.0081 166.247 85.9187L160.077 75.5481C158.643 73.108 160.294 69.9707 163.162 69.8835L175.198 69.4042C176.501 69.3606 177.674 68.7942 178.543 67.792L186.494 58.7286C188.363 56.5935 191.839 57.2471 192.838 59.9051V59.8615Z"
            stroke="#E56E2E"
            strokeWidth="5.6"
            strokeLinejoin="round"
          />
          <path
            d="M59.3577 120.783C59.736 119.752 60.994 119.254 62.0038 119.661L62.4008 119.901C65.3098 121.664 68.024 123.846 70.6273 126.175L70.6275 126.175C83.5501 137.733 92.9687 152.741 99.202 168.986C93.4426 156.463 85.7435 144.843 75.8893 135.236L75.8855 135.232C71.3586 130.852 66.4014 126.728 60.8305 123.641L60.738 123.59L60.6389 123.553C59.4631 123.117 58.9112 121.89 59.3413 120.826L59.3498 120.805L59.3577 120.783Z"
            fill="#E56E2E"
            stroke="#C74601"
            strokeWidth="2.8"
          />
          <path
            d="M175.744 120.026L175.701 120.046L175.66 120.069C161.61 127.911 149.924 140.983 141.385 154.632L141.385 154.632L141.378 154.642C137.877 160.348 134.825 166.315 132.215 172.484C132.998 170.304 133.835 168.141 134.728 165.999L134.731 165.992C136.062 162.749 137.617 159.424 139.211 156.325C140.098 154.609 141.47 152.121 142.43 150.481C149.508 138.51 158.491 127.649 169.433 119.397C170.897 118.301 172.425 117.25 173.981 116.316C174.892 115.798 176.088 116.107 176.654 117.102C177.244 118.191 176.791 119.531 175.744 120.026Z"
            fill="#E56E2E"
            stroke="#C74601"
            strokeWidth="2.8"
          />
          <path
            d="M73.6827 147.507C75.6837 149.348 77.592 151.303 79.4049 153.355C77.6781 151.652 75.8798 150.031 74.0097 148.511L74.0097 148.511L73.999 148.503L73.9907 148.496C73.7046 148.268 73.4025 148.028 73.1326 147.823C73.0549 147.759 73.0181 147.706 72.9998 147.672C72.9805 147.636 72.9708 147.602 72.9676 147.565C72.9605 147.484 72.9861 147.37 73.0589 147.268C73.0928 147.22 73.1283 147.187 73.1564 147.168C73.1701 147.158 73.1807 147.153 73.1877 147.15C73.1924 147.148 73.1954 147.147 73.197 147.147C73.1984 147.147 73.2008 147.147 73.2042 147.148C73.2164 147.151 73.2694 147.168 73.3576 147.241L73.6827 147.507Z"
            fill="#E56E2E"
            stroke="#C74601"
            strokeWidth="2.8"
          />
          <path
            d="M159.733 146.879C159.74 146.866 159.748 146.854 159.757 146.843L159.788 146.976L159.733 146.879Z"
            fill="#E56E2E"
            stroke="#C74601"
            strokeWidth="2.8"
          />
          <path
            d="M143.34 106.342C139.177 113.3 135.847 120.678 132.782 128.171C128.24 139.602 124.645 151.453 122.147 163.495C119.574 175.614 118.136 187.924 117.719 200.348C117.719 200.73 117.417 200.998 117.038 200.998C116.66 200.998 116.395 200.692 116.395 200.348C116.092 187.847 116.887 175.27 118.741 162.883C121.239 146.292 125.629 129.93 131.949 114.371C133.463 110.701 135.09 107.031 136.907 103.475L137.323 102.711C138.231 100.99 140.351 100.34 142.054 101.296C143.832 102.252 144.438 104.622 143.302 106.342H143.34Z"
            fill="#E56E2E"
          />
          <path
            d="M98.9402 112.129C101.691 117.326 103.983 122.676 105.893 128.217C107.726 133.72 109.178 139.452 109.445 145.261C109.445 145.834 108.605 145.949 108.529 145.338C107.803 139.682 106.046 134.217 103.83 128.982C101.614 123.784 98.9784 118.664 95.9605 113.887C94.7381 111.861 97.7178 110.065 98.9402 112.129Z"
            fill="#E56E2E"
          />
        </svg>
      ),
    },
    {
      title: qualificationsTitle,
      description: qualificationsDescription,
      color: "teal" as const,
      items: qualificationsItems.map((qualification) => qualification.item),
      icon: (
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 224 224"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M139.837 163.641H110.117V214.998H139.837V163.641Z"
            stroke="#00797D"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M208.921 53.8525H179.201V214.998H208.921V53.8525Z"
            stroke="#00797D"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M70.7493 193.512H41.0293V214.998H70.7493V193.512Z"
            stroke="#00797D"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M105.576 94.9905C105.576 94.9905 118.976 92.6846 131.854 99.6545C147.079 108.773 141.396 130.469 137.225 141.631C135.661 145.771 131.75 148.496 127.37 148.496C120.957 148.496 116.056 142.836 116.942 136.443L118.611 124.285C118.819 122.818 118.35 121.35 117.359 120.25C115.847 118.573 113.449 118.101 111.415 119.097C95.8776 126.748 77.9413 127.115 74.0308 108.511C69.8596 88.5446 83.7289 59.7219 83.7289 59.7219C83.7289 59.7219 71.0588 52.1755 56.3552 61.3464C56.3552 61.3464 52.549 63.9143 48.6385 61.4512C44.4151 58.8834 43.7894 53.014 47.4392 49.6077C56.7724 40.9085 75.8036 28.5933 102.343 37.6594C123.303 44.3672 138.789 60.9272 156.882 56.8396C156.882 56.8396 161.835 55.9487 163.921 60.2983C166.006 64.6479 163.921 60.4031 163.973 60.4556C165.329 63.3902 164.338 66.9014 161.574 68.5783C154.327 72.9803 139.05 79.1117 122.104 66.7442C122.104 66.7442 116.108 78.8497 115.17 92.7894"
            stroke="#06A6AB"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M100.055 123.866C100.055 123.866 86.6027 158.4 69.6571 177.79C68.0407 179.624 65.7466 180.673 63.296 180.673C56.8306 180.673 52.7115 173.755 55.8399 168.043C62.4617 155.885 71.6384 136.233 75.4447 114.904"
            stroke="#06A6AB"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M124.713 45.2056C134.936 45.2056 143.223 36.8764 143.223 26.6018C143.223 16.3272 134.936 7.99805 124.713 7.99805C114.49 7.99805 106.203 16.3272 106.203 26.6018C106.203 36.8764 114.49 45.2056 124.713 45.2056Z"
            stroke="#06A6AB"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M24.8801 70.7443L18.0413 90.0474C17.423 91.7924 18.3293 93.7107 20.0655 94.3321L50.1264 105.091C51.8626 105.712 53.7712 104.801 54.3895 103.056L61.2283 83.7531C61.8465 82.0081 60.9402 80.0898 59.204 79.4684L29.1431 68.7098C27.4069 68.0885 25.4983 68.9994 24.8801 70.7443Z"
            stroke="#00797D"
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          <path
            d="M42.7553 66.9012L50.055 69.5214C51.567 70.0455 52.3491 71.7224 51.8277 73.2946L50.7328 76.3865L37.9062 71.7749L39.0012 68.6829C39.5226 67.1632 41.1911 66.3771 42.7553 66.9012Z"
            stroke="#00797D"
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          <path
            d="M23.6113 73.8711C23.6113 73.8711 37.8456 87.7584 59.9531 86.9199"
            stroke="#00797D"
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          <path
            d="M39.3613 84.0901L41.9683 85.0333C42.5418 85.243 42.8547 85.8718 42.6461 86.4483C42.4376 87.0247 41.8119 87.3392 41.2383 87.1295L38.6313 86.1863C38.0578 85.9766 37.7449 85.3478 37.9535 84.7713C38.1621 84.1949 38.7877 83.8804 39.3613 84.0901Z"
            stroke="#00797D"
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      ),
    },
    {
      title: responsibilitiesTitle,
      description: responsibilitiesDescription,
      color: "orange" as const,
      items: responsibilitiesItems.map((responsibility) => responsibility.item),
      icon: (
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 224 224"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M33.2012 115.059C35.6764 87.9183 52.091 64.8162 75.2365 53.0046"
            stroke="#E56E2E"
            strokeWidth="5.6"
            strokeMiterlimit="10"
            strokeLinecap="round"
          />
          <path
            d="M146.539 53.3088C169.12 65.1204 185.231 87.7014 187.923 114.277"
            stroke="#E56E2E"
            strokeWidth="5.6"
            strokeMiterlimit="10"
            strokeLinecap="round"
          />
          <path
            d="M74.2344 190.966C85.0906 196.741 97.4667 199.998 110.624 199.998C123.782 199.998 138.416 196.133 149.924 189.359"
            stroke="#E56E2E"
            strokeWidth="5.6"
            strokeMiterlimit="10"
            strokeLinecap="round"
          />
          <path
            d="M161.131 152.665V181.456C161.131 182.975 162.39 184.235 163.91 184.235H202.992C204.512 184.235 205.772 182.975 205.772 181.456V152.665"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeMiterlimit="10"
          />
          <path
            d="M213.11 159.482L183.798 130.171C183.711 130.084 183.624 130.084 183.537 130.084C183.45 130.084 183.364 130.084 183.277 130.171L153.965 159.482"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M61.7315 145.239H17.9592C15.1771 145.239 12.9219 147.494 12.9219 150.276V177.981C12.9219 180.763 15.1771 183.018 17.9592 183.018H61.7315C64.5135 183.018 66.7688 180.763 66.7688 177.981V150.276C66.7688 147.494 64.5135 145.239 61.7315 145.239Z"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeMiterlimit="10"
          />
          <path
            d="M32.3759 132.646H47.7049C50.3104 132.646 52.4382 134.774 52.4382 137.379V142.981C52.4382 144.24 51.396 145.282 50.1367 145.282H29.9441C28.6848 145.282 27.6426 144.24 27.6426 142.981V137.379C27.6426 134.774 29.7704 132.646 32.3759 132.646Z"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeMiterlimit="10"
          />
          <path
            d="M12.9219 154.619C12.9219 154.619 38.1083 166.126 66.7688 154.619"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeMiterlimit="10"
          />
          <path
            d="M42.3637 160.004H37.3264C36.7988 160.004 36.3711 160.431 36.3711 160.959V162.088C36.3711 162.616 36.7988 163.043 37.3264 163.043H42.3637C42.8913 163.043 43.3191 162.616 43.3191 162.088V160.959C43.3191 160.431 42.8913 160.004 42.3637 160.004Z"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeMiterlimit="10"
          />
          <path
            d="M110.366 29.4684C110.366 29.4684 110.279 29.4684 110.279 29.3815C109.932 28.7301 106.501 22.998 98.7283 22.998C90.9552 22.998 86.4824 30.2934 86.4824 35.5913C86.4824 40.8891 87.2206 45.7961 93.8647 51.6585C99.9876 57.0432 108.629 64.9031 109.888 66.0755C111.148 67.248 110.149 66.2058 110.279 66.2058H110.323C110.453 66.2058 110.54 66.1624 110.627 66.0755C111.843 65.0333 121.005 57.0866 127.258 51.6585C133.512 46.2304 134.727 40.9325 134.727 35.6347C134.727 30.3368 130.646 23.0849 122.351 23.0849C114.057 23.0849 111.104 28.513 110.583 29.3815C110.062 30.25 110.453 29.4684 110.41 29.4684H110.366Z"
            stroke="#C74601"
            strokeWidth="5.6"
            strokeMiterlimit="10"
          />
        </svg>
      ),
    },
  ];

  return (
    <section
      id={id}
      aria-label="Workplace Environment"
      className={cn("w-full bg-white", className)}
    >
      <div className="px-4 py-6 md:px-6 md:py-10 lg:mx-auto lg:max-w-lg lg:px-3">
        <div className="mb-8 text-center">
          <h2 className="mb-4 font-montserrat text-heading-4 font-medium text-secondary-500 md:text-heading-3 lg:text-heading-2">
            A Supportive and Nurturing Workplace
          </h2>
          <p className="font-montserrat text-subheading-3-400 text-neutral-600 lg:text-subheading-2">
            Fostering Growth and Excellence in Caregiving
          </p>
        </div>

        <div className="lg:hidden">
          <div
            ref={emblaRef}
            className="overflow-hidden"
          >
            <div className="flex gap-4 md:gap-8">
              {cards.map((card, index) => (
                <div
                  key={card.title}
                  className={cn(
                    "flex-[0_0_40%]",
                    index === 0 && "ml-4" // Add left margin to first slide
                  )}
                >
                  <Card
                    title={card.title}
                    description={card.description}
                    icon={card.icon}
                    items={card.items}
                    color={card.color}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="hidden md:flex-col md:items-center md:gap-8 lg:flex lg:flex-row lg:justify-center lg:gap-16">
          {cards.map((card) => (
            <Card
              key={card.title}
              title={card.title}
              description={card.description}
              icon={card.icon}
              items={card.items}
              color={card.color}
              className="max-w-[302px]"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CareersCIEnvironment;
